import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';

const PAGE_TITLES = {
  '/':                    { title: 'Dashboard',           icon: '⊞' },
  '/inventory':           { title: 'Inventory',            icon: '📦' },
  '/workers':             { title: 'Workers',              icon: '👷' },
  '/stationery':          { title: 'Stationery',           icon: '✏️' },
  '/salary-deductions':   { title: 'Salary Deductions',    icon: '💰' },
  '/notifications':       { title: 'Notifications',        icon: '🔔' },
  '/requirements':        { title: 'Requirements',         icon: '📋' },
  '/transfers':           { title: 'Transfers',            icon: '🔄' },
  '/groups':              { title: 'Groups & Sites',       icon: '🏢' },
  '/safety-kits':         { title: 'Materials',            icon: '📦' },
  '/users':               { title: 'User Management',      icon: '👥' },
  '/permission-groups':   { title: 'Permission Groups',    icon: '🔐' },
  '/audit-logs':          { title: 'Audit Logs',           icon: '📝' },
  '/site-history':        { title: 'Site History',         icon: '🕐' },
};

export default function TopBar({ onMenuClick = () => {} }) {
  const { user } = useAuth();
  const { unreadCount } = useNotifications();
  const location = useLocation();
  const navigate = useNavigate();

  // Same "back to Portal" behavior as the top-left home icon — a real
  // cross-origin redirect on a standalone MaterialWatch build (separate
  // subdomain/container), or an in-app navigate on a combined build.
  const goHome = () => {
    if (process.env.REACT_APP_PORTAL_URL) {
      window.location.href = process.env.REACT_APP_PORTAL_URL;
    } else {
      navigate('/');
    }
  };

  // ── Profile dropdown ──────────────────────────────────────────────────────
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Page title ────────────────────────────────────────────────────────────
  const path = location.pathname;
  const base = '/' + path.split('/')[1];
  const meta = PAGE_TITLES[path] || PAGE_TITLES[base] || { title: 'MaterialWatch', icon: '📦' };

  const displayName = user?.fullName || user?.username;
  const initials = displayName?.[0]?.toUpperCase();

  return (
    <header className="bg-white border-b border-slate-200 px-6 py-0 flex items-center justify-between h-16 sticky top-0 z-10 shadow-sm">
        {/* Left — home + page title. On a standalone MaterialWatch build
            (separate subdomain/container), "/" has no module launcher — it's
            a real cross-origin link back to Portal instead. */}
        <div className="flex items-center gap-3">
          {/* Hamburger — mobile/tablet only, toggles the off-canvas sidebar drawer */}
          <button
            type="button"
            onClick={onMenuClick}
            title="Menu"
            className="md:hidden w-8 h-8 rounded-lg flex items-center justify-center text-slate-500 hover:bg-slate-100 hover:text-indigo-600 transition-colors flex-shrink-0"
          >
            <svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
              <path fillRule="evenodd" d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd"/>
            </svg>
          </button>
          {process.env.REACT_APP_PORTAL_URL ? (
            <a
              href={process.env.REACT_APP_PORTAL_URL}
              title="Back to Modules"
              className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-indigo-600 transition-colors flex-shrink-0"
            >
              <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z"/>
              </svg>
            </a>
          ) : (
            <Link
              to="/"
              title="Back to Modules"
              className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-indigo-600 transition-colors flex-shrink-0"
            >
              <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z"/>
              </svg>
            </Link>
          )}
          <div className="w-px h-5 bg-slate-200 flex-shrink-0" />
          <div>
            <h1 className="text-base font-bold text-slate-900 leading-none">{meta.title}</h1>
            {user?.site?.name && (
              <p className="text-xs text-slate-400 mt-0.5">
                <span className="text-primary-500 font-medium">{user.site.name}</span>
                {user.site.group?.name && <span className="text-slate-300"> · {user.site.group.name}</span>}
              </p>
            )}
          </div>
        </div>

        {/* Right — actions */}
        <div className="flex items-center gap-3">
          {/* Notification bell */}
          <Link
            to="/notifications"
            className="relative w-9 h-9 rounded-lg flex items-center justify-center text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors"
          >
            <svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
              <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z"/>
            </svg>
            {unreadCount > 0 && (
              <span className="absolute top-1 right-1 bg-red-500 text-white text-[9px] font-bold rounded-full min-w-[15px] h-[15px] flex items-center justify-center px-0.5 leading-none">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </Link>

          {/* Divider */}
          <div className="w-px h-6 bg-slate-200" />

          {/* Profile dropdown trigger */}
          <div className="relative" ref={dropdownRef}>
            <button
              type="button"
              onClick={() => setShowDropdown((v) => !v)}
              className="flex items-center gap-2.5 pl-1 pr-2 py-1 rounded-lg hover:bg-slate-100 transition-colors"
            >
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white shadow-sm flex-shrink-0"
                style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)' }}
              >
                {initials}
              </div>
              <div className="hidden sm:block text-left">
                <p className="text-sm font-semibold text-slate-800 leading-none">{displayName}</p>
              </div>
              {/* Chevron */}
              <svg
                viewBox="0 0 20 20" fill="currentColor"
                className={`w-3.5 h-3.5 text-slate-400 transition-transform ${showDropdown ? 'rotate-180' : ''}`}
              >
                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd"/>
              </svg>
            </button>

            {/* Dropdown menu */}
            {showDropdown && (
              <div className="absolute right-0 top-full mt-2 w-52 bg-white rounded-xl shadow-lg border border-slate-200 py-1.5 z-50">
                {/* User info header */}
                <div className="px-4 py-2.5 border-b border-slate-100 mb-1">
                  <p className="text-sm font-semibold text-slate-800 truncate">{displayName}</p>
                  {user?.email && <p className="text-xs text-slate-400 truncate">{user.email}</p>}
                </div>

                {/* Back Home — password management and sign-out both live in
                    the Portal now, not inside this module. */}
                <button
                  type="button"
                  onClick={() => { setShowDropdown(false); goHome(); }}
                  className="w-full flex items-center gap-3 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                >
                  <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-slate-400">
                    <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z"/>
                  </svg>
                  Back Home
                </button>
              </div>
            )}
          </div>
        </div>
    </header>
  );
}
